import { motion } from 'framer-motion'
import { ExternalLink, Download } from 'lucide-react'
import { Link } from 'react-router-dom'
import Glitch from '@/components/Glitch'

function isInternal(href: string) {
    return href.startsWith('/')
        && !href.endsWith('.vsix')
        && !href.endsWith('.sh')
        && !href.endsWith('.ps1')
}

interface Props {
    title: string
    href?: string
    description: string
    dates: string
    image?: string
    video?: string
    links?: readonly { icon: React.ReactNode; type: string; href: string }[]
    iconLists?: readonly string[]
    className?: string
}

export function ProjectCard({
    title,
    href,
    description,
    dates,
    iconLists,
    links,
    className,
}: Props) {
    // Derive a slug-style directory name
    const slug = title.toLowerCase().replace(/\s+/g, '-')

    // Derive language labels from icon list
    const techLabels = (iconLists ?? []).map((icon) => {
        const name = icon.split('/').pop()?.split('.')[0] ?? ''
        return name
            .replace('react-logo', 'React')
            .replace('next-logo', 'Next.js')
            .replace('typescript', 'TypeScript')
            .replace('tailwindcss', 'Tailwind')
            .replace('javascript', 'JavaScript')
            .replace('python', 'Python')
            .replace('php-color', 'PHP')
            .replace('mysql', 'MySQL')
            .replace('aws', 'AWS')
    })

    const primaryLang = techLabels[0] ?? 'Web'

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className={['term-card font-mono flex flex-col h-full', className].filter(Boolean).join(' ')}
        >
            {/* ── Terminal titlebar ── */}
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-border">
                <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#333]" aria-hidden />
                    <span className="w-2.5 h-2.5 rounded-full bg-[#333]" aria-hidden />
                    <span className="w-2.5 h-2.5 rounded-full bg-primary/60" aria-hidden />
                </div>
                <span className="text-[10px] text-[#444] truncate max-w-[60%]">{slug}/</span>
                <span className="text-[10px] text-[#444]">{dates}</span>
            </div>

            {/* ── Project name as directory ── */}
            <p className="text-sm text-primary font-bold mb-3">
                <Glitch text={`${slug}/`} trigger="hover">{slug}/</Glitch>
            </p>

            {/* ── Metadata block ── */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs mb-4">
                <span className="text-[#555]">Language:</span>
                <span className="text-foreground">{primaryLang}</span>

                <span className="text-[#555]">Status:</span>
                <span className="flex items-center gap-1.5 text-primary">
                    <span className="status-dot-online" />
                    Active
                </span>

                <span className="text-[#555]">Year:</span>
                <span className="text-foreground">{dates}</span>
            </div>

            {/* ── Description ── */}
            <p className="text-xs text-[#888] leading-relaxed mb-6 border-l border-[#222] pl-3">
                {description}
            </p>

            {/* ── Bottom Section (Tech tags & links) pinned to card bottom ── */}
            <div className="mt-auto space-y-4">
                {/* Tech stack tags */}
                {techLabels.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                        {techLabels.map((tech, i) => (
                            <span
                                key={i}
                                className="text-[10px] px-2 py-0.5 border border-[#222] text-[#555] rounded-sm"
                            >
                                {tech}
                            </span>
                        ))}
                    </div>
                )}

                {/* Links */}
                <div className="flex gap-2 flex-wrap pt-1">
                    {links && links.length > 0 && links.map((link, i) => {
                        const isDownload =
                            link.type === 'Download' ||
                            link.href.endsWith('.vsix')
                        if (isDownload) {
                            return (
                                <a
                                    key={i}
                                    href={link.href}
                                    download
                                    className="btn-term text-xs flex items-center gap-1.5"
                                >
                                    <Download className="w-3 h-3" />
                                    [ {link.type.toLowerCase()} ]
                                </a>
                            )
                        }
                        if (isInternal(link.href)) {
                            return (
                                <Link
                                    key={i}
                                    to={link.href}
                                    className="btn-term text-xs flex items-center gap-1.5"
                                >
                                    <ExternalLink className="w-3 h-3" />
                                    [ {link.type.toLowerCase()} ]
                                </Link>
                            )
                        }
                        return (
                            <a
                                key={i}
                                href={link.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-term text-xs flex items-center gap-1.5"
                            >
                                <ExternalLink className="w-3 h-3" />
                                [ {link.type.toLowerCase()} ]
                            </a>
                        )
                    })}
                    {href && (() => {
                        const isDownload = href.endsWith('.vsix')
                        return isDownload ? (
                            <a
                                href={href}
                                download
                                className="btn-term-accent text-xs flex items-center gap-1.5"
                            >
                                <Download className="w-3 h-3" />
                                [ install extension ]
                            </a>
                        ) : (
                            <a
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-term-accent text-xs flex items-center gap-1.5"
                            >
                                <ExternalLink className="w-3 h-3" />
                                [ view site ]
                            </a>
                        )
                    })()}
                </div>
            </div>
        </motion.div>
    )
}
